import configuration from "react-global-configuration";
import {
  extractCustomIconUrl,
  resolveBuiltinSocialIcon,
  normalizeSocialNetworkKey,
  socialNetworkMeta,
  SOCIAL_NETWORKS,
} from "./socialNetworks";

/**
 * Brand Links — configuration-driven social & external links by placement.
 * Prefer configData.brand_links over legacy facebook_link / twitter_link fields.
 */

export function getBrandLinksConfig() {
  const config = configuration.get("configData.brand_links");
  if (config && typeof config === "object") {
    return config;
  }
  return {
    version: 1,
    placements: [],
    links: [],
    by_placement: {},
  };
}

/**
 * Active Brand Links for a placement (header, footer, donation_page, referral_page, …).
 */
export function getBrandLinksForPlacement(placement) {
  const config = getBrandLinksConfig();
  const byPlacement =
    config.by_placement && typeof config.by_placement === "object"
      ? config.by_placement
      : {};

  let links = Array.isArray(byPlacement[placement])
    ? byPlacement[placement]
    : [];

  // Legacy footer fallback when Brand Links API is not yet migrated.
  if ((!links || !links.length) && placement === "footer") {
    links = legacyFooterBrandLinks();
  }

  return [...links]
    .filter((link) => link && link.url)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
}

function legacyFooterBrandLinks() {
  const legacy = [
    {
      id: "legacy-facebook",
      display_name: "Facebook",
      url: configuration.get("configData.facebook_link"),
      visibleFlag: configuration.get("configData.is_facebook_link_visible"),
      builtin_icon: "fab fa-facebook-f",
      display_order: 10,
      open_in_new_tab: true,
      css_class: "facebook",
    },
    {
      id: "legacy-x",
      display_name: "X",
      url: configuration.get("configData.twitter_link"),
      visibleFlag: configuration.get("configData.is_twitter_link_visible"),
      builtin_icon: "fab fa-x-twitter",
      display_order: 20,
      open_in_new_tab: true,
      css_class: "x",
    },
    {
      id: "legacy-linkedin",
      display_name: "LinkedIn",
      url: configuration.get("configData.linkedin_link"),
      visibleFlag: configuration.get("configData.is_linkedin_link_visible"),
      builtin_icon: "fab fa-linkedin-in",
      display_order: 30,
      open_in_new_tab: true,
      css_class: "linkedin",
    },
    {
      id: "legacy-pinterest",
      display_name: "Pinterest",
      url: configuration.get("configData.pinterest_link"),
      visibleFlag: configuration.get("configData.is_pinterest_link_visible"),
      builtin_icon: "fab fa-pinterest-p",
      display_order: 40,
      open_in_new_tab: true,
      css_class: "pinterest",
    },
  ];

  return legacy
    .filter((item) => item.visibleFlag == 1 && item.url)
    .map(({ visibleFlag, ...link }) => link);
}

export function resolveBrandLinkIconClass(link) {
  if (!link) {
    return "fas fa-link";
  }
  if (resolveBrandLinkIconUrl(link)) {
    return null;
  }
  const icon = (link.builtin_icon || link.icon || "").trim();
  if (icon) {
    if (/^(fas|fab|far|fal|fad)\s/.test(icon)) {
      return icon;
    }
    if (icon.indexOf("fa-") === 0) {
      return `fas ${icon}`;
    }
    return icon.includes(" ") ? icon : `fas ${icon}`;
  }
  return resolveBuiltinSocialIcon(link);
}

/**
 * Custom uploaded / Brand Link asset URL, or null for builtin FA.
 */
export function resolveBrandLinkIconUrl(link) {
  if (!link) {
    return null;
  }
  return extractCustomIconUrl(link);
}

/**
 * Canonical entity platform list from Platform Admin Brand Links (order + labels).
 * Entities only supply destination URLs for these platforms.
 */
export function getEntityPlatformRegistry(platformsFromApi) {
  if (Array.isArray(platformsFromApi) && platformsFromApi.length) {
    return platformsFromApi
      .map((platform, index) => {
        const network = normalizeSocialNetworkKey(
          (platform && (platform.network || platform.css_class || platform.display_name)) ||
            ""
        );
        if (!network) {
          return null;
        }
        return {
          network,
          display_name:
            (platform && platform.display_name) ||
            socialNetworkMeta(network).label,
          display_order:
            platform && platform.display_order != null
              ? Number(platform.display_order)
              : index * 10,
          css_class:
            (platform && platform.css_class) ||
            socialNetworkMeta(network).css_class,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.display_order - b.display_order);
  }

  const config = getBrandLinksConfig();
  const platformLinks = [];
  if (Array.isArray(config.links)) {
    platformLinks.push(...config.links);
  }

  const seen = {};
  const out = [];
  platformLinks
    .slice()
    .sort(
      (a, b) =>
        Number((a && a.display_order) || 0) - Number((b && b.display_order) || 0)
    )
    .forEach((link, index) => {
      if (!link || link.is_enabled === false) {
        return;
      }
      const network = normalizeSocialNetworkKey(
        link.css_class || link.display_name || link.legacy_key || ""
      );
      if (!network || seen[network]) {
        return;
      }
      seen[network] = true;
      out.push({
        network,
        display_name: link.display_name || socialNetworkMeta(network).label,
        display_order:
          link.display_order != null ? Number(link.display_order) : index * 10,
        css_class: link.css_class || socialNetworkMeta(network).css_class,
      });
    });

  if (out.length) {
    return out;
  }

  // Fallback when Brand Links config is empty.
  return Object.keys(SOCIAL_NETWORKS).map((network, index) => ({
    network,
    display_name: socialNetworkMeta(network).label,
    display_order: (index + 1) * 10,
    css_class: socialNetworkMeta(network).css_class,
  }));
}

/**
 * Overlay shared Brand Link artwork onto entity social links and sort by
 * Platform Admin Brand Link display_order. Destination URLs remain entity-owned.
 */
export function applySharedPlatformBrandIcons(links) {
  if (!Array.isArray(links) || !links.length) {
    return [];
  }

  const config = getBrandLinksConfig();
  const platformLinks = [];
  if (Array.isArray(config.links)) {
    platformLinks.push(...config.links);
  }
  const byPlacement =
    config.by_placement && typeof config.by_placement === "object"
      ? config.by_placement
      : {};
  Object.keys(byPlacement).forEach((key) => {
    if (Array.isArray(byPlacement[key])) {
      platformLinks.push(...byPlacement[key]);
    }
  });

  const orderIndex = {};
  platformLinks
    .slice()
    .sort(
      (a, b) =>
        Number((a && a.display_order) || 0) - Number((b && b.display_order) || 0)
    )
    .forEach((platform, index) => {
      const network = normalizeSocialNetworkKey(
        (platform && (platform.css_class || platform.display_name)) || ""
      );
      if (network && orderIndex[network] == null) {
        orderIndex[network] = Number(
          platform.display_order != null ? platform.display_order : index * 10
        );
      }
    });

  const enriched = links.map((link) => {
    if (!link || !link.url) {
      return link;
    }

    const network = normalizeSocialNetworkKey(
      link.css_class || link.network || link.display_name || link.label || ""
    );
    const platform = findPlatformBrandLink(platformLinks, network, link);
    if (!platform) {
      return {
        ...link,
        _registry_order:
          link.display_order != null
            ? Number(link.display_order)
            : orderIndex[network] != null
              ? orderIndex[network]
              : 9999,
      };
    }

    const next = { ...link };
    const platformIcon = extractCustomIconUrl(platform);
    if (platformIcon) {
      next.icon_type = "custom";
      next.icon_url = platformIcon;
      if (platform.custom_icon) {
        next.custom_icon = platform.custom_icon;
      }
    } else if (platform.builtin_icon) {
      next.icon_type = "builtin";
      next.builtin_icon = platform.builtin_icon;
      // Do not keep a stale entity custom icon when Platform owns artwork.
      delete next.icon_url;
      delete next.custom_icon;
    }
    if (platform.css_class) {
      next.css_class = platform.css_class;
    }
    if (platform.display_name && !next.display_name) {
      next.display_name = platform.display_name;
    }
    next._registry_order =
      platform.display_order != null
        ? Number(platform.display_order)
        : orderIndex[network] != null
          ? orderIndex[network]
          : 9999;
    return next;
  });

  return enriched.sort(
    (a, b) =>
      Number((a && a._registry_order) || 9999) -
      Number((b && b._registry_order) || 9999)
  );
}

function findPlatformBrandLink(platformLinks, network, entityLink) {
  const meta = socialNetworkMeta(network);
  const label = String(
    (entityLink && (entityLink.display_name || entityLink.label)) ||
      meta.label ||
      ""
  )
    .trim()
    .toLowerCase();

  for (let i = 0; i < platformLinks.length; i += 1) {
    const platform = platformLinks[i];
    if (!platform) continue;
    const css = String(platform.css_class || "")
      .trim()
      .toLowerCase();
    const name = String(platform.display_name || "")
      .trim()
      .toLowerCase();
    if (
      css &&
      (css === meta.css_class ||
        css === network ||
        (network === "x" && css === "twitter"))
    ) {
      return platform;
    }
    if (label && name === label) {
      return platform;
    }
    if (
      network &&
      (name === network ||
        (network === "x" && (name === "twitter" || name === "x")))
    ) {
      return platform;
    }
  }
  return null;
}

export function brandLinkTargetProps(link) {
  const openNewTab = link && link.open_in_new_tab !== false;
  return {
    target: openNewTab ? "_blank" : undefined,
    rel: openNewTab ? "noopener noreferrer" : undefined,
  };
}
